import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import AppStyles from '../styles/AppStyles';
import { useTranslation } from 'react-i18next';

export default function Feedback() {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

  const MESSAGE_MIN_LENGTH = 10;
  const MESSAGE_MAX_LENGTH = 3000;
  
  // Validation: subject must have input, message must have at least 10 characters and max 3000
  const isFormValid = subject.trim().length > 0 && 
                      message.trim().length >= MESSAGE_MIN_LENGTH && 
                      message.trim().length <= MESSAGE_MAX_LENGTH;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!subject.trim()) {
      setError(t('feedback.validation.subjectRequired', 'Please enter a subject'));
      return;
    }

    if (!message.trim()) {
      setError(t('feedback.validation.messageRequired', 'Please enter your feedback message'));
      return;
    }

    if (message.trim().length < MESSAGE_MIN_LENGTH) {
      setError(t('feedback.validation.messageTooShort', 'Please provide more details (at least 10 characters)'));
      return;
    }

    if (message.trim().length > MESSAGE_MAX_LENGTH) {
      setError(t('feedback.validation.messageTooLong', 'Message cannot exceed 3000 characters'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const token = localStorage.getItem('vetToken');
      const response = await fetch(`${API_BASE}/api/user/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subject: subject.trim(),
          message: message.trim(),
          source: "vet portal"
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.msg || t('feedback.error.submitFailed', 'Failed to submit feedback. Please try again.'));
      }

      setSuccess(true);
      setSubject('');
      setMessage('');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err.message || t('feedback.error.submitFailed', 'Failed to submit feedback. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          color: AppStyles.colors.text.secondary,
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        {t('feedback.description', "We'd love to hear your thoughts, suggestions, or report any issues you've encountered. Your feedback helps us improve Pawwi.")}
      </Typography>

      {/* Feedback Form */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'transparent',
          border: `1px solid ${AppStyles.colors.border.light}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
          }
        }}
      >
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('feedback.success.message', 'Your feedback has been submitted successfully. We appreciate your input!')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('feedback.subject', 'Subject')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('feedback.subjectPlaceholder', 'Brief description of your feedback')}
            margin="normal"
            required
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              }
            }}
          />

          <Box sx={{ position: 'relative', mb: 3 }}>
            <TextField
              fullWidth
              label={t('feedback.message', 'Message')}
              value={message}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue.length <= MESSAGE_MAX_LENGTH) {
                  setMessage(newValue);
                }
              }}
              placeholder={t('feedback.messagePlaceholder', 'Please provide detailed feedback...')}
              margin="normal"
              required
              multiline
              minRows={8}
              maxRows={20}
              disabled={loading}
              inputProps={{
                maxLength: MESSAGE_MAX_LENGTH
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 24,
                right: 24,
                color: message.length > MESSAGE_MAX_LENGTH
                  ? '#d32f2f'
                  : AppStyles.colors.text.secondary,
                fontSize: '0.75rem',
                pointerEvents: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: '2px 6px',
                borderRadius: '4px',
              }}
            >
              {message.length} / {MESSAGE_MAX_LENGTH}
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={!isFormValid || loading}
            sx={{
              backgroundColor: AppStyles.colors.action || AppStyles.colors.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: AppStyles.colors.action || AppStyles.colors.primary.dark,
              },
              px: 4,
              py: 1.5,
            }}
          >
            {loading ? t('common.loading', 'Submitting...') : t('feedback.submit', 'Submit Feedback')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

